import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { creatorToken, setCreatorToken, inputCls, btnGold, cardCls } from "@/lib/creators";

export default function CreatorLogin() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [state, setState] = useState(token ? "verifying" : "idle");
  const [err, setErr] = useState("");
  const tried = useRef(false);

  useEffect(() => {
    if (!token || tried.current) return;
    tried.current = true;
    api.post("/creators/verify", { token })
      .then(({ data }) => { setCreatorToken(data.token); setState("done"); })
      .catch((e) => { setErr(formatApiErrorDetail(e.response?.data?.detail)); setState("idle"); });
  }, [token]);

  if (state === "done" || (!token && creatorToken() && state === "idle" && !err && params.get("switch") !== "1")) return <Navigate to="/creators/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setState("sending");
    try {
      await api.post("/creators/login", { email });
      setState("sent");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
      setState("idle");
    }
  };

  return (
    <PublicShell testId="creator-login-page">
      <div className="px-6 pt-10 max-w-md mx-auto fade-up">
        <p className="text-[11px] uppercase tracking-[0.35em] gold-text mb-2">Creator Program</p>
        <h1 className="text-3xl font-light mb-4">Creator sign in</h1>
        {state === "verifying" && <p className="flex items-center gap-2 text-[#A7B0C0]"><Loader2 size={16} className="animate-spin" /> Signing you in…</p>}
        {state === "sent" ? (
          <div className={`${cardCls} text-center`} data-testid="creator-login-sent">
            <MailCheck size={32} className="gold-text mx-auto mb-3" />
            <p className="text-base">If {email} belongs to a creator account, a sign-in link is on its way. It expires in 15 minutes.</p>
          </div>
        ) : state !== "verifying" && (
          <form onSubmit={submit} className="space-y-3" data-testid="creator-login-form">
            <p className="text-sm text-[#A7B0C0]">Enter the email you applied with and we'll send you a one-time sign-in link. No password needed.</p>
            <input data-testid="creator-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
            {err && <p data-testid="creator-login-error" className="text-sm text-red-300">{err}</p>}
            <button data-testid="creator-login-submit" disabled={state === "sending"} className={`${btnGold} w-full py-3`}>
              {state === "sending" ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Email me a sign-in link"}
            </button>
          </form>
        )}
      </div>
    </PublicShell>
  );
}
